import { Autocomplete, type AutocompleteProps, Divider, ListItem } from '@mui/material';
import { TextInput } from '@buoyhealth/common.buoy-input';
import { KeyboardArrowDown } from '@mui/icons-material';
import { useMemo } from 'react';

// Given autocomplete suggestion and search query,
// returns formatted string with text following query in bold
const boldAutocomplete = (suggestion: string, query: string) => {
  if (suggestion && query && suggestion.toLowerCase().indexOf(query.toLowerCase()) === 0) {
    const queryLength = query.length;
    return (
      <span>
        {suggestion.substring(0, queryLength)}
        <b className="bp-font-semibold">{suggestion.substring(queryLength)}</b>
      </span>
    );
  }
  return suggestion;
};

interface ISearchDropDownProps extends Partial<AutocompleteProps<any, false, false, true>> {
  options: Array<{ name: string; token: string }>;
  searchString?: string;
  placeholder: string;
  autocomplete?: string;
}

const SearchDropDown = ({
  searchString = '',
  placeholder,
  autocomplete,
  value,
  ...rest
}: ISearchDropDownProps) => {
  const valueObject = useMemo(
    () => rest.options?.find((option) => option.token === value) || null,
    [value, rest.options],
  );

  return (
    <Autocomplete
      blurOnSelect
      disablePortal
      freeSolo
      getOptionLabel={(option) => option.name || ''}
      handleHomeEndKeys
      forcePopupIcon
      multiple={false}
      openOnFocus
      renderInput={(params) => (
        <TextInput
          {...params}
          placeholder={placeholder}
          inputProps={{
            ...params.inputProps,
            className: `!bp-placeholder-gray-70 ${params.inputProps.className}`,
            autoComplete: autocomplete,
          }}
          InputProps={{
            ...params.InputProps,
            sx: {
              backgroundColor: 'white',
            },
          }}
        />
      )}
      renderOption={(props, option, state) => (
        <>
          {state.index !== 0 && <Divider />}
          <ListItem {...props} className={`${props.className} !bp-py-3`}>
            <span className="bp-text-blue-50">{boldAutocomplete(option.name, searchString)}</span>
          </ListItem>
        </>
      )}
      popupIcon={<KeyboardArrowDown color="primary" />}
      componentsProps={{
        paper: {
          elevation: 0,
          sx: {
            outline: '1px solid #8F8F8F',
            paddingX: '16px',
          },
        },
      }}
      value={valueObject}
      {...rest}
    />
  );
};

export default SearchDropDown;
